import { Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import { vaultSwapContractsConfig } from "@config/contractsConfig";
import {
  APPROVAL_AMOUNT,
  MAX_FRACTION_DIGITS,
  WrapBridgeMappings,
  isNumber,
  toFixedWithoutRounding,
} from "@constants/common";
import useDappChain from "@hooks/useDappChain";
import useDappConfig from "@hooks/useDappConfig";

import UTMLink from "@components/UTMLink";
import {
  CONNECT_WALLET,
  VAULT_APPROVE_WRAPPING,
  VAULT_MAX_WRAP_UNWRAP,
  WRAP_INITIATED,
  WRAP_FAILED,
  WRAP_SUCCESSFUL,
  UNWRAP_INITIATED,
  UNWRAP_FAILED,
  UNWRAP_SUCCESSFUL,
} from "@constants/analytics";
import useVaultSwapTokensBalancesChain from "@hooks/useVaultSwapTokenBalancesChain";
import {
  Button,
  Card,
  Input,
  Typography,
} from "@kelp-dao/kernel-design-system";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { FaRegCircleCheck } from "react-icons/fa6";
import { useDispatch } from "react-redux";
import { updateUnwrapTxnState } from "@store/slices/globalSlice";
import { formatNumber, isErrorRejectedByUser, trackEvent } from "utils";
import { formatUnits, parseEther } from "viem";
import {
  useAccount,
  useBalance,
  usePublicClient,
  useSwitchChain,
  useWriteContract,
} from "wagmi";
import { TChainSelection } from "./types";

const txnInitialState = {
  open: false,
  hash: "",
  error: "",
  hasUserDenied: false,
};

const WrapUnwrapAgEthAccordianV2 = ({
  type,
  sourceChain,
  destinationChain,
}: {
  type: string;
  sourceChain: TChainSelection;
  destinationChain: TChainSelection;
}) => {
  const { chain: wagmiChain } = useAccount();
  const queryClient = useQueryClient();
  const { switchChain } = useSwitchChain();
  const { chain } = useDappChain();
  const chainIdParam = type === "wrap" ? destinationChain.id : sourceChain.id;

  const { vaultSwapConfig, isVaultSwapSupported } = useDappConfig();
  const { isConnected } = useAccount();
  const client = usePublicClient();
  const dispatch = useDispatch();

  // const { agETH, wagETH } = vaultSwapConfig.tokens;
  const { agETH = null, wagETH = null } =
    isConnected &&
    chainIdParam &&
    vaultSwapContractsConfig[chainIdParam]?.tokens
      ? vaultSwapContractsConfig[chainIdParam].tokens
      : {};

  const fromToken = type === "wrap" ? agETH : wagETH;

  const [swapAmount, setSwapAmount] = useState("");
  const [error, setError] = useState("");
  const [isPending, setPending] = useState(false);
  const { writeContract } = useWriteContract();
  const { openConnectModal } = useConnectModal();
  const toast = useToast();

  const hasToSwitch =
    type === "wrap"
      ? destinationChain?.id !== chain.id
      : sourceChain?.id !== chain.id;

  const { data, queryKey } = useBalance({
    token: agETH?.address,
    address: wagETH?.address,
    chainId: chainIdParam,
    query: {
      refetchInterval: 10_000,
    },
  });

  const unwrapLimit =
    data && agETH?.decimals
      ? Number(formatUnits(data.value, agETH.decimals))
      : 0;

  const agETHSelected = agETH?.symbol === fromToken?.symbol;

  const [isTxModalOpen, setIsTxModalOpen] = useState(txnInitialState);
  const [txnCompleted, setTxnCompleted] = useState(false);

  const {
    agETHBalance,
    agETHAllowance,
    wagETHBalance,
    wagETHAllowance,
    invalidateCache,
  } = useVaultSwapTokensBalancesChain(chainIdParam);

  const fromTokenBal = formatUnits(
    agETHSelected ? agETHBalance : wagETHBalance,
    18
  );

  const toTokenBal = formatUnits(
    agETHSelected ? wagETHBalance : agETHBalance,
    18
  );

  const tokenAllowance = formatUnits(
    agETHSelected ? agETHAllowance : wagETHAllowance,
    18
  );

  const hasSufficientAllowance =
    !agETHSelected || Number(tokenAllowance) >= Number(swapAmount);

  const toToken = agETHSelected ? wagETH : agETH;

  const chainIdForSwitch =
    type === "wrap" ? destinationChain.id : sourceChain?.id;

  const hasBalance = Number(toFixedWithoutRounding(fromTokenBal, 4)) > 0;

  const checkInput = useCallback(
    (value = swapAmount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > Number(fromTokenBal)) {
        error = `You can't wrap/unwrap more than available balance in your wallet.`;
      } else if (!agETHSelected && Number(unwrapLimit) === 0) {
        error =
          "You cannot unwrap agETH as there is no agETH.axl available at the moment";
      } else if (!agETHSelected && Number(value) > Number(unwrapLimit)) {
        error = `You can't unwrap more than available limit of ${unwrapLimit} ${fromToken?.symbol}.`;
        error = `You can unwrap a maximum of ${+unwrapLimit.toFixed(
          4
        )} agETH at the moment`;
      }
      return error;
    },
    [
      swapAmount,
      isConnected,
      fromTokenBal,
      agETHSelected,
      unwrapLimit,
      fromToken?.symbol,
    ]
  );

  useEffect(() => {
    if (swapAmount !== "") {
      const error = checkInput(swapAmount);
      setError(error);
    }
  }, [checkInput, swapAmount, fromTokenBal]);

  useEffect(() => {
    if (txnCompleted) {
      setTxnCompleted(false);
    }
  }, [chain, sourceChain, destinationChain]);

  const handleApprove = () => {
    if (!client || !fromToken || !wagETH) {
      return;
    }
    setIsTxModalOpen({
      open: true,
      hash: "",
      error: "",
      hasUserDenied: false,
    });

    setPending(true);
    writeContract(
      {
        address: fromToken.address,
        abi: fromToken.abi,
        functionName: "approve",
        args: [wagETH.address, parseEther(APPROVAL_AMOUNT)],
      },
      {
        onError: (error: any) => {
          trackEvent(VAULT_APPROVE_WRAPPING, {
            error: error?.reason ?? error?.error ?? error?.message ?? "",
            dapp: "vault",
          });
          setPending(false);
          setIsTxModalOpen({
            open: true,
            // @ts-ignore
            hash: error.transactionHash,
            // @ts-ignore
            error: error?.reason ?? error?.error ?? error?.message ?? "",
            hasUserDenied: isErrorRejectedByUser(error),
          });
        },
        onSuccess: async (hash) => {
          trackEvent(VAULT_APPROVE_WRAPPING, {
            agETH_Balance: formatUnits(agETHBalance, 18),
            wagETH_Balance: formatUnits(wagETHBalance, 18),
            dapp: "vault",
          });
          setIsTxModalOpen({
            open: true,
            hash,
            error: "",
            hasUserDenied: false,
          });
          await client.waitForTransactionReceipt({
            hash,
          });
          invalidateCache();
          queryClient.invalidateQueries({ queryKey });
          setPending(false);
        },
      }
    );
  };

  const handleSwap = () => {
    if (!client || !fromToken || !wagETH || !agETH) {
      return;
    }
    const error = checkInput(swapAmount);
    if (error) {
      setError(error);
      return;
    }

    if (wagmiChain?.id !== vaultSwapConfig.chain.id) {
      toast({
        description: "chain and config mismatch. Please refresh",
        status: "error",
        duration: 5000,
        position: "top",
      });
      // eslint-disable-next-line no-console
      console.log(`chain and config mismatch. Please refresh`);
      return;
    }

    setIsTxModalOpen({
      open: true,
      hash: "",
      error: "",
      hasUserDenied: false,
    });

    setPending(true);

    const functionName =
      fromToken.symbol === agETH.symbol ? "deposit" : "withdraw";

    // Track wrap/unwrap_initiated event
    const isWrap = type === "wrap";
    trackEvent(isWrap ? WRAP_INITIATED : UNWRAP_INITIATED, {
      source_chain: sourceChain.title || "",
      destination_chain: destinationChain.title || "",
      source_token_symbol: isWrap ? agETH?.symbol || "" : wagETH?.symbol || "",
      destination_token_symbol: isWrap
        ? wagETH?.symbol || ""
        : agETH?.symbol || "",
      amount: swapAmount,
      from_chain_id: String(sourceChain.id),
      to_chain_id: String(destinationChain.id),
      dapp: "vault",
    });

    writeContract(
      {
        address: wagETH.address,
        abi: wagETH.abi,
        functionName,
        args: [agETH.address, parseEther(swapAmount)],
      },
      {
        onError: (error) => {
          setPending(false);
          setIsTxModalOpen({
            open: true,
            // @ts-ignore
            hash: error.transactionHash,
            // @ts-ignore
            error: error?.reason ?? error?.error ?? error?.message ?? "",
            hasUserDenied: isErrorRejectedByUser(error),
          });
          setTxnCompleted(true);

          // Track wrap/unwrap_failed event
          trackEvent(isWrap ? WRAP_FAILED : UNWRAP_FAILED, {
            source_chain: sourceChain.title || "",
            destination_chain: destinationChain.title || "",
            source_token_symbol: isWrap
              ? agETH?.symbol || ""
              : wagETH?.symbol || "",
            destination_token_symbol: isWrap
              ? wagETH?.symbol || ""
              : agETH?.symbol || "",
            amount: swapAmount,
            from_chain_id: String(sourceChain.id),
            to_chain_id: String(destinationChain.id),
            error_reason: error?.message || String(error),
            dapp: "vault",
          });
        },
        onSuccess: async (hash) => {
          setIsTxModalOpen({
            open: true,
            hash,
            error: "",
            hasUserDenied: false,
          });
          await client.waitForTransactionReceipt({
            hash,
          });
          invalidateCache();
          setPending(false);
          queryClient.invalidateQueries({ queryKey });
          setSwapAmount("");
          setTxnCompleted(true);
          if (!isWrap) {
            dispatch(
              updateUnwrapTxnState({
                success: true,
              })
            );
          }

          // Track wrap/unwrap_successful event
          trackEvent(isWrap ? WRAP_SUCCESSFUL : UNWRAP_SUCCESSFUL, {
            source_chain: vaultSwapConfig.chain.name || "",
            destination_chain: vaultSwapConfig.chain.name || "",
            source_token_symbol: isWrap
              ? agETH?.symbol || ""
              : wagETH?.symbol || "",
            destination_token_symbol: isWrap
              ? wagETH?.symbol || ""
              : agETH?.symbol || "",
            amount: swapAmount,
            from_chain_id: String(vaultSwapConfig.chain.id),
            to_chain_id: String(vaultSwapConfig.chain.id),
            hash,
            dapp: "vault",
          });
        },
      }
    );
  };

  const handleMaxClick = () => {
    trackEvent(VAULT_MAX_WRAP_UNWRAP, {
      agETH_Balance: formatUnits(agETHBalance, 18),
      wagETH_Balance: formatUnits(wagETHBalance, 18),
      dapp: "vault",
    });
    setSwapAmount(toFixedWithoutRounding(fromTokenBal, 4));
  };

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setSwapAmount(val);
      } else {
        if (txnCompleted) setTxnCompleted(false);
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setSwapAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleConnectWallet = () => {
    openConnectModal?.();
    trackEvent(CONNECT_WALLET, {
      type: "wrap_ageth_page",
      dapp: "vault",
    });
  };

  const getButtonText = () => {
    if (!isConnected) return "Connect Wallet";
    if (hasToSwitch)
      return `Switch to ${
        type === "wrap" ? destinationChain.title : sourceChain.title
      }`;
    if (!hasSufficientAllowance) return isPending ? "Approving" : "Approve";
    if (agETHSelected) return isPending ? "Wrapping Token" : "Wrap Token";
    return isPending ? "Unwrapping Token" : "Unwrap Token";
  };

  const handleButtonClick = () => {
    if (!isConnected) {
      handleConnectWallet();
    } else if (!isVaultSwapSupported || hasToSwitch) {
      switchChain({ chainId: Number(chainIdForSwitch) });
    } else if (!hasSufficientAllowance) {
      handleApprove();
    } else {
      handleSwap();
    }
  };

  if (!fromToken || !toToken) {
    return null;
  }

  return (
    <>
      <Card
        variant="non-bordered"
        className="w-full p-4 md:p-8 flex flex-col gap-4 rounded-2xl"
      >
        <div className="flex justify-between items-center">
          <Typography
            variant="xsmall"
            type="body"
            className="text-text-secondary"
          >
            On {type === "wrap" ? destinationChain?.title : sourceChain?.title}
          </Typography>
        </div>
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className="flex flex-col gap-1 w-full">
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary"
            >
              From
            </Typography>
            <Input
              value={swapAmount}
              onChange={(e) => handleChange(e.target.value)}
              containerClassName="w-full"
              inputClassName="bg-[#f4f4f7]"
              rightElementClassName="bg-bg-primary h-[80%] flex items-center"
              rightElement={
                <Typography
                  variant="xsmall"
                  type="body"
                  className="text-text-secondary"
                >
                  {WrapBridgeMappings.TOKENS[fromToken.symbol]}
                </Typography>
              }
              placeholder="0"
            />
            <div className="flex justify-end items-center gap-2 h-[40px]">
              <Typography
                variant="small"
                type="body"
                className="text-text-secondary"
              >
                Balance:{" "}
                <strong>
                  {formatNumber(Number(fromTokenBal), false, 4, true)}{" "}
                  {WrapBridgeMappings.TOKENS[fromToken?.symbol]}
                </strong>
              </Typography>
              <Button variant="outline" size="xsmall" onClick={handleMaxClick}>
                MAX
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full">
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary"
            >
              To
            </Typography>
            <Input
              value={swapAmount}
              onChange={(e) => handleChange(e.target.value)}
              disabled
              containerClassName="w-full"
              inputClassName="bg-[#f4f4f7]"
              rightElementClassName="bg-bg-primary bg-opacity-50 h-[80%] flex items-center"
              rightElement={
                <Typography
                  variant="xsmall"
                  type="body"
                  className="text-text-secondary"
                >
                  {WrapBridgeMappings.TOKENS[toToken.symbol]}
                </Typography>
              }
              placeholder="0"
            />
            <div className="flex justify-end items-center gap-2 h-[40px]">
              <Typography
                variant="small"
                type="body"
                className="text-text-secondary"
              >
                Balance:{" "}
                <strong>
                  {formatNumber(Number(toTokenBal), false, 4, true)}{" "}
                  {WrapBridgeMappings.TOKENS[toToken?.symbol]}
                </strong>
              </Typography>
            </div>
          </div>
        </div>

        {!agETHSelected && (
          <Flex justify="start" gap={1} width="100%">
            <Flex gap={1} alignItems={"center"}>
              <Text fontSize={"10px"} lineHeight={"15px"} fontWeight={400}>
                Unwrap Limit:
              </Text>
            </Flex>
            <Text fontSize={"10px"} lineHeight={"15px"} fontWeight={700}>
              {+unwrapLimit.toFixed(4)}
            </Text>
          </Flex>
        )}

        {!txnCompleted ? (
          <Button
            variant="primary"
            disabled={
              !!error ||
              isPending ||
              (!isVaultSwapSupported && !hasToSwitch && !swapAmount)
            }
            onClick={handleButtonClick}
          >
            {isPending && <Spinner size="sm" color="white" />}
            {getButtonText()}
          </Button>
        ) : (
          <>
            <Button variant="primary">
              {!isTxModalOpen.error && <FaRegCircleCheck color="#fff" />}

              {isTxModalOpen.error
                ? "TRANSACTION FAILED"
                : agETHSelected
                ? "WRAPPED SUCCESSFULLY"
                : "UNWRAPPED SUCCESSFULLY"}
            </Button>

            <Flex
              fontSize="10px"
              lineHeight="15px"
              fontWeight="400"
              textAlign="center"
              color={isTxModalOpen.error ? "#FF616A" : "textSecondary"}
              justifyContent={"center"}
            >
              {isTxModalOpen.error ? (
                <Flex gap={1}>
                  <Text
                    borderBottom="1px solid"
                    borderColor="#FF616A"
                    onClick={() => {
                      setIsTxModalOpen(txnInitialState);
                      setTxnCompleted(false);
                    }}
                    cursor={"pointer"}
                  >
                    Click here
                  </Text>
                  <Text> to reset & try again</Text>
                </Flex>
              ) : (
                <UTMLink
                  isExternal
                  borderBottom="1px solid"
                  borderColor="textSecondary"
                  onClick={() => {
                    const url = `${chain.blockExplorers?.default.url}/tx/${isTxModalOpen.hash}`;
                    window.open(url, "_blank");
                  }}
                >
                  View Transaction
                </UTMLink>
              )}
            </Flex>
          </>
        )}

        {error && !txnCompleted && (
          <Flex alignItems="center" justifyContent={"center"}>
            <BaseImage
              src="/assets/transactions/error.svg"
              width={20}
              height={20}
              alt="error-icon"
            />
            <Text ml="0.5rem" color="error" fontSize="12px" fontWeight="400">
              {error ? error : "error"}
            </Text>
          </Flex>
        )}

        {!hasBalance && !error && !txnCompleted && (
          <Flex alignItems="center" justifyContent={"center"}>
            <BaseImage
              src="/assets/transactions/error.svg"
              width={20}
              height={20}
              alt="error-icon"
            />
            <Text ml="0.5rem" color="error" fontSize="12px" fontWeight="400">
              Not enough balance for this transaction
            </Text>
          </Flex>
        )}
      </Card>
    </>
  );
};

export default WrapUnwrapAgEthAccordianV2;
